import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft,
  Ticket,
  User,
  Phone,
  MapPin,
  Package,
  Calendar,
  Clock,
  AlertCircle,
  CheckCircle,
  Send,
  Edit2,
  Save,
  X,
  ExternalLink,
  Trash2,
  History,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TicketDetail = () => {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [sending, setSending] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [formData, setFormData] = useState({
    status: '',
    pending_reason: '',
    notes: ''
  });

  useEffect(() => {
    fetchTicket();
    fetchHistory();
  }, [ticketId]);

  const fetchHistory = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/tickets/${ticketId}/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setHistory(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching history:', error);
    }
  };

  const fetchTicket = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/tickets/${ticketId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const ticketData = response.data.data;
      setTicket(ticketData);
      setFormData({
        status: ticketData.status,
        pending_reason: ticketData.pending_reason || '',
        notes: ticketData.notes || ''
      });
      setLoading(false);
    } catch (error) {
      console.error('Error fetching ticket:', error);
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    // Validate PENDING status must have reason
    if (formData.status === 'PENDING' && !formData.pending_reason) {
      alert('Catatan wajib diisi untuk status PENDING');
      return;
    }

    setUpdating(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${BACKEND_URL}/api/tickets/${ticketId}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      await fetchTicket();
      await fetchHistory(); // Refresh history after update
      setEditMode(false);
      alert('Tiket berhasil diupdate');
    } catch (error) {
      console.error('Error updating ticket:', error);
      alert(error.response?.data?.detail || 'Gagal update tiket');
    } finally {
      setUpdating(false);
    }
  };

  const handleSendWhatsApp = async () => {
    if (!window.confirm('Kirim notifikasi WhatsApp ke teknisi?')) return;

    setSending(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${BACKEND_URL}/api/tickets/${ticketId}/send-wa`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      alert('WhatsApp berhasil dikirim!');
      await fetchTicket();
    } catch (error) {
      console.error('Error sending WhatsApp:', error);
      alert(error.response?.data?.detail || 'Gagal kirim WhatsApp');
    } finally {
      setSending(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Hapus tiket ${ticket.ticket_number}?\n\nTindakan ini tidak bisa dibatalkan!`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${BACKEND_URL}/api/tickets/${ticketId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      alert('Tiket berhasil dihapus');
      navigate('/app/tickets');
    } catch (error) {
      console.error('Error deleting ticket:', error);
      alert(error.response?.data?.detail || 'Gagal menghapus tiket');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      'OPEN': { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', icon: Clock },
      'PENDING': { bg: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-200', icon: AlertCircle },
      'CLOSE': { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200', icon: CheckCircle }
    };
    
    const badge = badges[status] || badges['OPEN'];
    const Icon = badge.icon;
    
    return (
      <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium border ${badge.bg} ${badge.text} ${badge.border}`}>
        <Icon size={16} />
        {status}
      </span>
    );
  };

  const getTypeEmoji = (type) => {
    const emojis = {
      'PEMASANGAN': '🔧',
      'PROBLEM': '⚠️',
      'REQUEST': '📝'
    };
    return emojis[type] || '🎫';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-slate-300">Loading ticket...</p>
        </div>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="p-8 text-center">
        <p className="text-gray-600 dark:text-slate-300">Ticket not found</p>
        <button
          onClick={() => navigate('/app/tickets')}
          className="mt-4 text-indigo-600 hover:text-indigo-700 dark:text-indigo-400"
        >
          Back to Tickets
        </button>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 w-full">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/app/tickets')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 text-sm dark:text-slate-300"
        >
          <ArrowLeft size={16} />
          Back to Tickets
        </button>
        
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className="p-2 sm:p-3 bg-indigo-100 rounded-lg flex-shrink-0 dark:bg-indigo-900/40">
              <Ticket className="text-indigo-600 dark:text-indigo-400" size={24} />
            </div>
            <div className="min-w-0 flex-1">
              <h1 className="text-lg sm:text-2xl font-bold text-gray-900 truncate dark:text-slate-100">
                {getTypeEmoji(ticket.ticket_type)} {ticket.ticket_type}
              </h1>
              <p className="text-xs sm:text-sm text-gray-600 mt-0.5 sm:mt-1 truncate dark:text-slate-300">
                No. Tiket: <span className="font-mono font-medium">{ticket.ticket_number}</span>
              </p>
            </div>
          </div>
          
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            {getStatusBadge(ticket.status)}
            
            {!editMode ? (
              <button
                onClick={() => setEditMode(true)}
                className="flex items-center justify-center gap-1.5 px-3 py-2 text-xs sm:text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700/50"
              >
                <Edit2 size={14} />
                <span className="hidden xs:inline sm:inline">Edit Status</span>
                <span className="xs:hidden sm:hidden">Edit</span>
              </button>
            ) : (
              <div className="flex gap-2">
                <button
                  onClick={() => setEditMode(false)}
                  className="px-3 py-2 text-xs sm:text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700/50"
                  aria-label="Cancel"
                >
                  <X size={14} />
                </button>
                <button
                  onClick={handleUpdate}
                  disabled={updating}
                  className="flex items-center justify-center gap-1.5 px-3 py-2 text-xs sm:text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 font-medium"
                >
                  <Save size={14} />
                  Simpan
                </button>
              </div>
            )}

            {ticket.assigned_to_phone && (
              <button
                onClick={handleSendWhatsApp}
                disabled={sending}
                className="flex items-center justify-center gap-1.5 px-3 py-2 text-xs sm:text-sm border border-green-300 text-green-700 rounded-lg hover:bg-green-50 disabled:opacity-50 font-medium transition-colors dark:text-green-300 dark:hover:bg-green-900/30"
              >
                <Send size={14} />
                {sending ? 'Sending...' : 'Send WA'}
              </button>
            )}

            <button
              onClick={handleDelete}
              className="flex items-center justify-center gap-1.5 px-3 py-2 text-xs sm:text-sm border border-red-300 text-red-600 rounded-lg hover:bg-red-50 font-medium transition-colors dark:text-red-400 dark:hover:bg-red-900/30"
            >
              <Trash2 size={14} />
              Delete
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Customer Info */}
          <div className="bg-white rounded-lg border border-gray-200 p-6 dark:bg-slate-800 dark:border-slate-700">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 dark:text-slate-100">Informasi Customer</h2>
            
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <User className="text-gray-400 mt-1" size={18} />
                <div>
                  <p className="text-xs text-gray-500 dark:text-slate-400">Nama Customer</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-slate-100">{ticket.customer_name}</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Phone className="text-gray-400 mt-1" size={18} />
                <div>
                  <p className="text-xs text-gray-500 dark:text-slate-400">No. HP</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-slate-100">{ticket.customer_phone}</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <MapPin className="text-gray-400 mt-1" size={18} />
                <div className="flex-1">
                  <p className="text-xs text-gray-500 dark:text-slate-400">Alamat</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-slate-100">{ticket.customer_address}</p>
                  {ticket.customer_coordinates && (
                    <a
                      href={ticket.customer_coordinates.includes('http') 
                        ? ticket.customer_coordinates 
                        : `https://maps.google.com/?q=${ticket.customer_coordinates}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 mt-1 text-xs text-indigo-600 hover:text-indigo-700 dark:text-indigo-400"
                    >
                      Buka Google Maps
                      <ExternalLink size={12} />
                    </a>
                  )}
                </div>
              </div>

              {ticket.service_package && (
                <div className="flex items-start gap-3">
                  <Package className="text-gray-400 mt-1" size={18} />
                  <div>
                    <p className="text-xs text-gray-500 dark:text-slate-400">Paket Layanan</p>
                    <p className="text-sm font-medium text-gray-900 dark:text-slate-100">{ticket.service_package}</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* PPPoE Credentials */}
          {(ticket.pppoe_username || ticket.pppoe_password) && (
            <div className="bg-white rounded-lg border border-gray-200 p-6 dark:bg-slate-800 dark:border-slate-700">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 dark:text-slate-100">PPPoE Credentials</h2>
              
              <div className="space-y-3">
                {ticket.pppoe_username && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500 dark:text-slate-400">Username</span>
                    <span className="text-sm font-mono font-medium text-gray-900 bg-gray-50 px-3 py-1 rounded dark:text-slate-100 dark:bg-slate-900">
                      {ticket.pppoe_username}
                    </span>
                  </div>
                )}
                
                {ticket.pppoe_password && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500 dark:text-slate-400">Password</span>
                    <span className="text-sm font-mono font-medium text-gray-900 bg-gray-50 px-3 py-1 rounded dark:text-slate-100 dark:bg-slate-900">
                      {ticket.pppoe_password}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Edit Status Form */}
          {editMode && (
            <div className="bg-white rounded-lg border border-gray-200 p-6 dark:bg-slate-800 dark:border-slate-700">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 dark:text-slate-100">Update Status</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">
                    Status <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:border-slate-600"
                  >
                    <option value="OPEN">Open</option>
                    <option value="PENDING">Pending</option>
                    <option value="CLOSE">Close</option>
                  </select>
                </div>

                {formData.status === 'PENDING' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">
                      Alasan Pending <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={formData.pending_reason}
                      onChange={(e) => setFormData({ ...formData, pending_reason: e.target.value })}
                      rows="3"
                      placeholder="Wajib diisi untuk status PENDING..."
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:border-slate-600"
                      required
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">
                    Catatan Tambahan
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows="3"
                    placeholder="Update catatan..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:border-slate-600"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Side Panel */}
        <div className="space-y-6">
          {/* Change History */}
          <div className="bg-white rounded-lg border border-gray-200 dark:bg-slate-800 dark:border-slate-700">
            <button
              onClick={() => setShowHistory(!showHistory)}
              className="w-full p-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-slate-700/50"
            >
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2 dark:text-slate-100">
                <History size={18} />
                Riwayat Perubahan ({history.length})
              </h2>
              {showHistory ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </button>
            
            {showHistory && (
              <div className="border-t border-gray-200 p-4 max-h-64 overflow-y-auto dark:border-slate-700">
                {history.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-4 dark:text-slate-400">Belum ada perubahan</p>
                ) : (
                  <div className="space-y-3">
                    {history.map((entry, index) => (
                      <div key={entry.id || index} className="border-l-2 border-indigo-200 pl-3 py-1">
                        <p className="text-xs text-gray-500 dark:text-slate-400">
                          {new Date(entry.timestamp).toLocaleString('id-ID')} - {entry.user_name}
                        </p>
                        {entry.changes?.map((change, idx) => (
                          <p key={idx} className="text-sm text-gray-700 dark:text-slate-200">
                            {change.field === 'status' && (
                              <>Status: <span className="line-through text-gray-400">{change.old_value}</span> → <span className="font-medium text-indigo-600 dark:text-indigo-400">{change.new_value}</span></>
                            )}
                            {change.field === 'assigned_to' && (
                              <>Teknisi: <span className="line-through text-gray-400">{change.old_value || '-'}</span> → <span className="font-medium text-indigo-600 dark:text-indigo-400">{change.new_value}</span></>
                            )}
                            {change.field === 'priority' && (
                              <>Prioritas: <span className="line-through text-gray-400">{change.old_value}</span> → <span className="font-medium text-indigo-600 dark:text-indigo-400">{change.new_value}</span></>
                            )}
                            {change.field === 'ticket' && (
                              <span className="font-medium text-green-600 dark:text-green-400">{change.new_value}</span>
                            )}
                          </p>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Timeline */}
          <div className="bg-white rounded-lg border border-gray-200 p-6 dark:bg-slate-800 dark:border-slate-700">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 dark:text-slate-100">Timeline</h2>
            
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <Calendar className="text-gray-400 mt-1" size={18} />
                <div>
                  <p className="text-xs text-gray-500 dark:text-slate-400">Dibuat</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-slate-100">
                    {new Date(ticket.created_at).toLocaleString('id-ID')}
                  </p>
                  <p className="text-xs text-gray-500 mt-1 dark:text-slate-400">oleh {ticket.created_by_name}</p>
                </div>
              </div>

              {ticket.updated_at && (
                <div className="flex items-start gap-3">
                  <Clock className="text-gray-400 mt-1" size={18} />
                  <div>
                    <p className="text-xs text-gray-500 dark:text-slate-400">Last Update</p>
                    <p className="text-sm font-medium text-gray-900 dark:text-slate-100">
                      {new Date(ticket.updated_at).toLocaleString('id-ID')}
                    </p>
                  </div>
                </div>
              )}

              {ticket.closed_at && (
                <div className="flex items-start gap-3">
                  <CheckCircle className="text-green-600 mt-1 dark:text-green-400" size={18} />
                  <div>
                    <p className="text-xs text-gray-500 dark:text-slate-400">Closed</p>
                    <p className="text-sm font-medium text-gray-900 dark:text-slate-100">
                      {new Date(ticket.closed_at).toLocaleString('id-ID')}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Assigned To */}
          <div className="bg-white rounded-lg border border-gray-200 p-6 dark:bg-slate-800 dark:border-slate-700">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 dark:text-slate-100">Assigned To</h2>
            
            {ticket.assigned_to_name ? (
              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-900 dark:text-slate-100">{ticket.assigned_to_name}</p>
                {ticket.assigned_to_phone && (
                  <p className="text-sm text-gray-600 dark:text-slate-300">{ticket.assigned_to_phone}</p>
                )}
                {ticket.assigned_to_2_name && (
                  <div className="pt-2 mt-2 border-t border-gray-100 dark:border-slate-700">
                    <p className="text-xs text-gray-500 dark:text-slate-400 mb-1">Teknisi ke-2</p>
                    <p className="text-sm font-medium text-gray-900 dark:text-slate-100">{ticket.assigned_to_2_name}</p>
                    {ticket.assigned_to_2_phone && (
                      <p className="text-sm text-gray-600 dark:text-slate-300">{ticket.assigned_to_2_phone}</p>
                    )}
                  </div>
                )}
                {ticket.wa_sent && (
                  <div className="mt-3 p-2 bg-green-50 border border-green-200 rounded-lg dark:bg-green-900/30">
                    <p className="text-xs text-green-700 dark:text-green-300">
                      ✓ WhatsApp terkirim
                    </p>
                    {ticket.wa_sent_at && (
                      <p className="text-xs text-green-600 mt-1 dark:text-green-400">
                        {new Date(ticket.wa_sent_at).toLocaleString('id-ID')}
                      </p>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm text-gray-500 italic dark:text-slate-400">Belum di-assign</p>
            )}
          </div>

          {/* Notes/Pending Reason */}
          {(ticket.notes || ticket.pending_reason) && (
            <div className="bg-white rounded-lg border border-gray-200 p-6 dark:bg-slate-800 dark:border-slate-700">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 dark:text-slate-100">Catatan</h2>
              
              {ticket.pending_reason && ticket.status === 'PENDING' && (
                <div className="mb-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg dark:bg-yellow-900/30">
                  <p className="text-xs text-yellow-700 font-medium mb-1 dark:text-yellow-300">Alasan Pending:</p>
                  <p className="text-sm text-yellow-900">{ticket.pending_reason}</p>
                </div>
              )}
              
              {ticket.notes && (
                <p className="text-sm text-gray-700 whitespace-pre-wrap dark:text-slate-200">{ticket.notes}</p>
              )}
            </div>
          )}

          {/* Public Link */}
          {ticket.public_link_token && !['CLOSE','CLOSED','COMPLETED'].includes((ticket.status||'').toUpperCase()) && (
            <div className="bg-white rounded-lg border border-gray-200 p-6 dark:bg-slate-800 dark:border-slate-700">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 dark:text-slate-100">Public Link</h2>
              <p className="text-xs text-gray-500 mb-2 dark:text-slate-400">Link untuk teknisi update status:</p>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={`${window.location.origin}/ticket/${ticket.public_link_token}`}
                  readOnly
                  className="flex-1 px-3 py-2 text-xs font-mono bg-gray-50 border border-gray-300 rounded dark:bg-slate-900 dark:border-slate-600"
                />
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(`${window.location.origin}/ticket/${ticket.public_link_token}`);
                    alert('Link copied!');
                  }}
                  className="px-3 py-2 bg-gray-200 rounded hover:bg-gray-300 text-xs dark:bg-slate-700"
                >
                  Copy
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TicketDetail;
